import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { from, Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { catchError, exhaustMap, switchMap } from 'rxjs/operators';
import {
  AuthActionsTypes,
  loginAuthInfoSuccessAction,
  loginOpenIdFailedAction,
  loginOpenIdSuccessAction,
  loginAuthInfoAction,
  invalidTokensAction,
  tokenRefreshFailedAction
} from '../actions/auth.actions';
import { OAuthService } from 'angular-oauth2-oidc';
import { AuthService } from 'src/app/angular-core/services/auth.service'
import { HttpErrorResponse } from '@angular/common/http';
import { clearPermissionDataAction, errorEventAction } from '../actions/app.actions';
import { getAuthInfoDevicesAction } from '../actions/devices.actions';
import { IAppState } from 'src/app/utils/interfaces/store.interfaces';
import { getAuthInfoLicensesAction } from '../actions/licenses.actions';
import { getAuthInfoSalesAction } from '../actions/sales.actions';
import { IAuthData, IAuthParams } from 'src/app/utils/interfaces/auth.interfaces';
import { getAuthInfoIdentityManagementAction } from '../actions/identitymanagement.actions';
import { getAuthInfoXpacProxyAction } from '../actions/xpacProxy.action';
import { AppInsightsService } from 'src/app/angular-core/services/appinsights.service';
import { IDataAction } from 'src/app/utils/interfaces/common.interfaces';
import { getAuthInfoProductionToolingAction } from '../actions/production.actions';
import { getAuthInfoAnalyticsAction } from '../actions/analytics.actions';
import { getAuthInfoInventoryManagementAction } from '../actions/inventorymanagement.actions';

const sessionStorageKey = 'oidc.requestedUrl';

// Enable to log all internal events from the OAuthService. Useful for debugging tokens.
const logServiceEvents = false;

@Injectable()
export class AuthEffects {
  public loginOpenIdEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AuthActionsTypes.LOGIN_OPENID),
        exhaustMap(({ idTokenHint }: IAuthParams) => from(this.oauthService.loadDiscoveryDocument())
          .pipe(
            switchMap(() => from(this.oauthService.tryLoginCodeFlow())),
            switchMap(() => {
              this.loginTap(idTokenHint);

              // Indicate that this action was succesful and trigger the auth info action
              return [loginOpenIdSuccessAction(), loginAuthInfoAction()];
            }),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.LoginOpenIdErrorHasOccurred" } }), loginOpenIdFailedAction())
            })
          )
        )
      )
  );

  public loginAuthInfoEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(AuthActionsTypes.LOGIN_AUTH_INFO),
        exhaustMap(() => this.loginCompleted(loginAuthInfoSuccessAction, invalidTokensAction))
        )
      );

  private loginTap(idTokenHint?: string): void {
    if (!this.oauthService.hasValidAccessToken()) {
      sessionStorage.setItem(sessionStorageKey, location.pathname + location.search);
      const params: any = {}
      if (idTokenHint) {
        params["id_token_hint"] = idTokenHint
      }
      this.oauthService.initLoginFlow(null, params);
    } else {
      const requestUrl = sessionStorage.getItem(sessionStorageKey);
      if (requestUrl) {
        sessionStorage.removeItem(sessionStorageKey);
        location.replace(location.origin + requestUrl);
      }
    }
  }

  private loginCompleted(action: (data: IDataAction<IAuthData>) => Action, missingTokensAction?: () => Action): Action[] {
    const accessToken = this.oauthService.getAccessToken();
    const idToken = this.oauthService.getIdToken();
    if (accessToken && idToken) {
      // Extract data from tokens
      const authData: IAuthData = this.getAuthData(accessToken, idToken);
      this.appInsightsService.setUser(authData.userData.email);
      // Update permissions that we have
      this.dispatchPermissionActions();
      return [action({ data: authData })];
    }
    else {
      if (missingTokensAction) {
        return [missingTokensAction()];
      }
      return [];
    }
  }

  private dispatchPermissionActions(): void {
    // We need to ensure that there is no old permission data, otherwise the UI may try to load things it shouldn't
    this.store.dispatch(clearPermissionDataAction());
    // Get the new permissions
    this.store.dispatch(getAuthInfoDevicesAction());
    this.store.dispatch(getAuthInfoLicensesAction());
    this.store.dispatch(getAuthInfoSalesAction());
    this.store.dispatch(getAuthInfoXpacProxyAction());
    this.store.dispatch(getAuthInfoIdentityManagementAction());
    this.store.dispatch(getAuthInfoProductionToolingAction());
    this.store.dispatch(getAuthInfoAnalyticsAction());
    this.store.dispatch(getAuthInfoInventoryManagementAction());
  }

  private getAuthData(accessToken: string, idToken: string): IAuthData {
    const { sub: userId, companyType, companyId, roles, impUserId: impUserIdAccess } = this.authService.getAccessTokenInfo(accessToken);
    const { name, email, companyName, impEmail, impName, impUserId } = this.authService.getIdentityTokenInfo(idToken);

    if (impUserIdAccess !== impUserId) {
      // Weird things can happen in this case, log it
      console.error(`imp_user_id mismatch: access=${impUserIdAccess}, id=${impUserId}`);
    }

    return ({ 
      accessToken: this.oauthService.getAccessToken(), 
      userData: { 
        userId,
        name,
        email,
        companyType,
        companyId,
        companyName, 
        roles,
        impersonator: impUserId ? {
          userId: impUserId,
          // Note that these two fields may be missing. Backend bug?
          email: impEmail ?? '',
          name: impName ?? '',
        } : undefined,
      }
    });
  }

  constructor(
    private actions$: Actions,
    private oauthService: OAuthService,
    private appInsightsService: AppInsightsService,
    private authService: AuthService,
    private store: Store<IAppState>
  ) {
    this.oauthService.events.subscribe((e) => {
      if (logServiceEvents) {
        console.log(e);
      }

      if (e.type === 'token_received') {
        // New token, log our auth data to the console
        const at = this.oauthService.getAccessToken();
        const it = this.oauthService.getIdToken();
        // Extract the auth data so getAuthData will automatically log an error if there are inconsistencies
        const authData = this.getAuthData(at, it);

        if (logServiceEvents) {
          console.log(authData);
        }
      }
      else if (e.type === 'token_refresh_error') {
        this.store.dispatch(tokenRefreshFailedAction());
      }
    });
  }
}
